import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CompanyShell } from "@/components/CompanyShell";
import { SlidersHorizontal, Loader2, Save, Info, RotateCcw, ScrollText, ChevronRight, Building2, Globe2, Upload, ImageOff } from "lucide-react";

const LABELS = {
  skills: "Zručnosti (Skills)",
  experience: "Skúsenosti (Experience)",
  responsibilities: "Zodpovednosti (Responsibilities)",
  education: "Vzdelanie / certifikáty",
  other: "Jazyk / lokalita / ostatné",
};

export default function CompanySettings() {
  const nav = useNavigate();
  const [weights, setWeights] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/company/settings")
      .then((r) => { setWeights(r.data.match_weights); setDefaults(r.data.defaults); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const total = weights ? Object.values(weights).reduce((a, b) => a + Number(b || 0), 0) : 0;

  const setW = (k, v) => setWeights((w) => ({ ...w, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/company/settings", { match_weights: weights });
      setWeights(data.match_weights);
      toast.success("Váhy uložené");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <CompanyShell>
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl bg-blue-700 flex items-center justify-center"><SlidersHorizontal className="text-white" size={20} /></div>
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight">Nastavenia</h1>
          <p className="text-slate-600">Váhy AI match skóre pre vašu firmu</p>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 rounded-2xl bg-blue-50 border border-blue-100 p-4 text-sm text-blue-800 max-w-2xl" data-testid="ai-disclaimer">
        <Info size={18} className="flex-shrink-0 mt-0.5" />
        <span>AI Match Score je <strong>odporúčanie – nie automatické rozhodnutie</strong> o prijatí alebo neprijatí kandidáta. Tieto váhy určujú, ako veľmi jednotlivé oblasti ovplyvnia skóre.</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-700" /></div>
      ) : (
        <div className="mt-6 max-w-2xl rounded-3xl bg-white border border-slate-200 shadow-sm p-6">
          <div className="space-y-5">
            {Object.keys(LABELS).map((k) => (
              <div key={k} data-testid={`weight-${k}`}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-slate-700">{LABELS[k]}</span>
                  <span className="text-sm font-bold text-blue-700 w-12 text-right">{weights[k]}%</span>
                </div>
                <input type="range" min="0" max="100" value={weights[k]}
                  data-testid={`weight-slider-${k}`}
                  onChange={(e) => setW(k, Number(e.target.value))}
                  className="w-full accent-blue-700" />
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
            <span className={`text-sm font-semibold ${total === 100 ? "text-emerald-600" : "text-amber-600"}`} data-testid="weight-total">
              Súčet: {total}% {total !== 100 && "(odporúčame 100%)"}
            </span>
            <button data-testid="reset-weights-btn" onClick={() => setWeights({ ...defaults })}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors">
              <RotateCcw size={15} /> Predvolené
            </button>
          </div>

          <button data-testid="save-weights-btn" onClick={save} disabled={saving}
            className="mt-5 inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-8 py-3.5 font-semibold transition-colors active:scale-95 disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Uložiť váhy
          </button>
        </div>
      )}

      <PublicProfileCard />

      <button data-testid="open-audit-btn" onClick={() => nav("/company/audit")}
        className="mt-5 w-full max-w-2xl flex items-center gap-3 rounded-3xl bg-white border border-slate-200 shadow-sm p-5 hover:shadow-md transition-all text-left">
        <div className="h-11 w-11 rounded-2xl bg-slate-800 flex items-center justify-center"><ScrollText className="text-white" size={20} /></div>
        <div className="flex-1">
          <div className="font-head font-semibold">Audit log</div>
          <div className="text-sm text-slate-500">História citlivých operácií vo firme</div>
        </div>
        <ChevronRight className="text-slate-400" size={20} />
      </button>
    </CompanyShell>
  );
}


function PublicProfileCard() {
  const [pp, setPp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logoRef = useRef(null);

  useEffect(() => {
    api.get("/company/public-profile").then((r) => setPp(r.data)).catch(() => setPp({ description: "", location: "", is_public: false, logo: "" }));
  }, []);

  const set = (k, v) => setPp((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/company/public-profile", { description: pp.description || "", location: pp.location || "", is_public: !!pp.is_public });
      setPp((p) => ({ ...p, ...data }));
      toast.success("Verejný profil uložený");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleVisibility = async (v) => {
    setPp((p) => ({ ...p, is_public: v }));
    try {
      const { data } = await api.put("/company/public-profile", { is_public: v });
      setPp((p) => ({ ...p, is_public: data.is_public }));
      toast.success(v ? "Firma je teraz viditeľná v Kandidátskom prístupe" : "Firma je skrytá z Kandidátskeho prístupu");
    } catch (e) {
      setPp((p) => ({ ...p, is_public: !v }));
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/company/public-profile/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo", data.logo);
      toast.success("Logo nahrané");
    } catch (err) { toast.error("Nahranie zlyhalo, skúste iný obrázok"); }
    finally { setUploading(false); if (logoRef.current) logoRef.current.value = ""; }
  };

  if (!pp) return null;

  return (
    <div className="mt-6 max-w-2xl rounded-3xl bg-white border border-slate-200 shadow-sm p-6" data-testid="public-profile-card">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl bg-blue-700 flex items-center justify-center"><Building2 className="text-white" size={20} /></div>
        <div>
          <h2 className="font-head text-xl font-bold tracking-tight">Verejný profil firmy</h2>
          <p className="text-sm text-slate-500">Údaje viditeľné pre kandidátov v Kandidátskom prístupe</p>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-4">
        {pp.logo ? (
          <img src={pp.logo} alt="Logo" className="h-16 w-16 rounded-2xl object-cover border border-slate-200" data-testid="company-logo-preview" />
        ) : (
          <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-300" data-testid="company-logo-empty"><ImageOff size={22} /></div>
        )}
        <div>
          <input ref={logoRef} type="file" accept="image/*" hidden data-testid="logo-input" onChange={uploadLogo} />
          <button data-testid="upload-logo-btn" onClick={() => logoRef.current?.click()} disabled={uploading}
            className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60">
            {uploading ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />} Nahrať logo
          </button>
        </div>
      </div>

      <label className="mt-5 block">
        <span className="text-sm font-semibold text-slate-700">Krátky popis firmy</span>
        <textarea data-testid="pp-description" value={pp.description || ""} onChange={(e) => set("description", e.target.value)} rows={3} maxLength={1000}
          placeholder="Čomu sa firma venuje…"
          className="mt-1.5 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
      </label>
      <label className="mt-4 block">
        <span className="text-sm font-semibold text-slate-700">Lokalita (verejná)</span>
        <input data-testid="pp-location" value={pp.location || ""} onChange={(e) => set("location", e.target.value)}
          placeholder="napr. Bratislava"
          className="mt-1.5 w-full rounded-xl border border-slate-300 px-4 py-2.5 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
      </label>

      <label className="mt-5 flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 p-4 cursor-pointer" data-testid="pp-public-toggle">
        <span className="flex items-center gap-2"><Globe2 size={17} className="text-blue-700" />
          <span><span className="text-sm font-semibold text-slate-800">Zobraziť firmu v Kandidátskom prístupe</span>
          <span className="block text-xs text-slate-500">Firma sa zobrazí v sekcii „Firmy" aj bez otvorených ponúk.</span></span>
        </span>
        <input type="checkbox" checked={!!pp.is_public} onChange={(e) => toggleVisibility(e.target.checked)} className="h-5 w-5 accent-blue-700" />
      </label>

      <button data-testid="save-public-profile-btn" onClick={save} disabled={saving}
        className="mt-5 inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-8 py-3.5 font-semibold transition-colors active:scale-95 disabled:opacity-60">
        {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Uložiť verejný profil
      </button>
    </div>
  );
}
